import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import {
  useReceiptBanking,
  useReceiptBankingStore,
} from "hooks";
import {
  ReceiptBankingForm,
  ReceiptBankingQuery,
  ReceiptBankingRow,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable";
import { Plus } from "lucide-react";

const todayString = () => new Date().toISOString().slice(0, 10);

const ReceiptBankings: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<ReceiptBankingRow | null>(null);
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [queryParams, setQueryParams] = useState<ReceiptBankingQuery>({
    page: 1,
    pageSize: 20,
  });
  const [showInlineAddRow, setShowInlineAddRow] = useState<any>("");
  const [confirmRow, setConfirmRow] = useState<ReceiptBankingRow | null>(null);
  const {
    rows,
    setRows,
    upsertRow,
    removeRow,
    editingId,
    setEditingId,
    isLoading,
    setLoading,
  } = useReceiptBankingStore();

  const {
    fetchList,
    createReceiptBanking,
    updateReceiptBanking,
    deleteReceiptBanking,
  } = useReceiptBanking();

  const {
    data: receiptBankingData,
    isFetching,
    refetch,
  } = fetchList(queryParams, true);

  const parseNumber = useCallback((value: unknown) => {
    if (value === null || value === undefined || value === "") return undefined;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const parsed = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : undefined;
  }, []);

  const withItemFields = useCallback(
    (receipt: ReceiptBankingRow): ReceiptBankingRow => {
      const firstItem = receipt.items?.[0];
      return {
        ...receipt,
        amount:
          receipt.amount ??
          (typeof firstItem?.amount === "number"
            ? firstItem.amount
            : firstItem?.amount
              ? Number(firstItem.amount)
              : undefined),
        partner_id: receipt.partner_id ?? firstItem?.partner_id,
        reason: receipt.reason ?? firstItem?.reason,
        business_field: receipt.business_field ?? firstItem?.business_field,
        item_description: receipt.item_description ?? firstItem?.description,
        vat_rate:
          receipt.vat_rate ??
          (typeof firstItem?.vat_rate === "number"
            ? firstItem.vat_rate
            : firstItem?.vat_rate
              ? Number(firstItem.vat_rate)
              : undefined),
        vat_amount:
          receipt.vat_amount ??
          (typeof firstItem?.vat_amount === "number"
            ? firstItem.vat_amount
            : firstItem?.vat_amount
              ? Number(firstItem.vat_amount)
              : undefined),
        pit_rate:
          receipt.pit_rate ??
          (typeof firstItem?.pit_rate === "number"
            ? firstItem.pit_rate
            : firstItem?.pit_rate
              ? Number(firstItem.pit_rate)
              : undefined),
        pit_amount:
          receipt.pit_amount ??
          (typeof firstItem?.pit_amount === "number"
            ? firstItem.pit_amount
            : firstItem?.pit_amount
              ? Number(firstItem.pit_amount)
              : undefined),
        net_amount:
          receipt.net_amount ??
          (typeof firstItem?.net_amount === "number"
            ? firstItem.net_amount
            : firstItem?.net_amount
              ? Number(firstItem.net_amount)
              : undefined),
      };
    },
    [],
  );

  useEffect(() => {
    if (receiptBankingData?.data) {
      const mapped =
        (receiptBankingData.data as ReceiptBankingRow[])?.map((item) =>
          withItemFields(item),
        ) ?? [];
      setRows(mapped);
    }
  }, [receiptBankingData, setRows, isFetching, withItemFields]);

  useEffect(() => {
    setLoading(
      Boolean(
        isFetching ||
        createReceiptBanking.isPending ||
        updateReceiptBanking.isPending ||
        deleteReceiptBanking.isPending,
      ),
    );
  }, [
    isFetching,
    createReceiptBanking.isPending,
    updateReceiptBanking.isPending,
    deleteReceiptBanking.isPending,
    setLoading,
  ]);

  const columns = useMemo<ColDef<ReceiptBankingRow>[]>(() => {
    return [
      {
        headerName: "Ngày thu",
        field: "receipt_date",
        minWidth: 140,
      },
      {
        headerName: "Số phiếu",
        field: "receipt_code",
        minWidth: 140,
        // editable: false,
      },
      {
        headerName: "Tài khoản NH",
        field: "bank_account_id",
        minWidth: 150,
      },
      {
        headerName: "Ngân hàng",
        field: "bank_name",
        minWidth: 180,
        flex: 1,
      },
      {
        headerName: "Người giao dịch",
        field: "transaction_person",
        minWidth: 180,
        flex: 1,
      },
      {
        headerName: "Người nộp",
        field: "payer_name",
        minWidth: 180,
        flex: 1,
      },
      {
        headerName: "Địa chỉ",
        field: "address",
        minWidth: 200,
        flex: 1,
      },
      {
        headerName: "Diễn giải",
        field: "description",
        minWidth: 200,
        flex: 1,
      },
      {
        headerName: "Kèm theo",
        field: "attachments",
        minWidth: 160,
      },
      {
        headerName: "Phiếu xuất",
        field: "export_invoice_id",
        minWidth: 140,
      },
      {
        headerName: "Số tiền",
        field: "amount",
        minWidth: 160,
      },
      {
        headerName: "Mã đối tác",
        field: "partner_id",
        minWidth: 140,
      },
      {
        headerName: "Lý do thu",
        field: "reason",
        minWidth: 180,
        flex: 1,
      },
      {
        headerName: "Lĩnh vực KD",
        field: "business_field",
        minWidth: 160,
      },
      {
        headerName: "Mô tả chi tiết",
        field: "item_description",
        minWidth: 220,
        flex: 1,
      },
      {
        headerName: "VAT %",
        field: "vat_rate",
        minWidth: 120,
      },
      {
        headerName: "Tiền VAT",
        field: "vat_amount",
        minWidth: 140,
        editable: false,
      },
      {
        headerName: "PIT %",
        field: "pit_rate",
        minWidth: 120,
      },
      {
        headerName: "Tiền PIT",
        field: "pit_amount",
        minWidth: 140,
        editable: false,
      },
      {
        headerName: "Net",
        field: "net_amount",
        minWidth: 140,
        editable: false,
      },
    ];
  }, []);

  const normalizePayload = useCallback(
    (row: Partial<ReceiptBankingRow>): ReceiptBankingForm => {
      const amount = parseNumber(row.amount ?? row.items?.[0]?.amount);
      const partnerId =
        row.partner_id ?? row.items?.[0]?.partner_id ?? undefined;
      const reason = row.reason ?? row.items?.[0]?.reason ?? "";
      const businessField =
        row.business_field ?? row.items?.[0]?.business_field ?? "";
      const detail =
        row.item_description ?? row.items?.[0]?.description ?? "";
      const vatRate = parseNumber(row.vat_rate ?? row.items?.[0]?.vat_rate);
      const pitRate = parseNumber(row.pit_rate ?? row.items?.[0]?.pit_rate);

      const exportInvoice =
        row.export_invoice_id !== undefined &&
          row.export_invoice_id !== null &&
          row.export_invoice_id !== ""
          ? Number(row.export_invoice_id)
          : undefined;

      const bankAccount =
        row.bank_account_id !== undefined &&
          row.bank_account_id !== null &&
          row.bank_account_id !== ""
          ? Number(row.bank_account_id)
          : undefined;

      const items =
        amount !== undefined || partnerId || reason || businessField || detail
          ? [
            {
              amount: amount ?? 0,
              partner_id: partnerId ? Number(partnerId) : undefined,
              reason: reason ? String(reason).trim() : undefined,
              business_field: businessField
                ? String(businessField).trim()
                : undefined,
              description: detail ? String(detail).trim() : undefined,
              vat_rate: vatRate,
              pit_rate: pitRate,
            },
          ]
          : [];

      return {
        receipt_date: String(row.receipt_date ?? "").trim(),
        bank_account_id: bankAccount,
        bank_name: row.bank_name ? String(row.bank_name).trim() : "",
        transaction_person: row.transaction_person
          ? String(row.transaction_person).trim()
          : "",
        payer_name: row.payer_name ? String(row.payer_name).trim() : "",
        address: row.address ? String(row.address).trim() : "",
        description: row.description ? String(row.description).trim() : "",
        attachments: row.attachments ? String(row.attachments).trim() : "",
        export_invoice_id: exportInvoice,
        items,
      };
    },
    [parseNumber],
  );

  type PersistMeta = {
    pinned?: boolean;
    onRevert?: () => void;
    onSuccess?: () => void;
    refetch?: boolean;
  };

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      receipt_date: todayString(),
      receipt_code: "",
      bank_account_id: "",
      bank_name: "",
      transaction_person: "",
      payer_name: "",
      address: "",
      description: "",
      attachments: "",
      export_invoice_id: undefined,
      amount: undefined,
      partner_id: undefined,
      reason: "",
      business_field: "",
      item_description: "",
      vat_rate: undefined,
      vat_amount: undefined,
      pit_rate: undefined,
      pit_amount: undefined,
      net_amount: undefined,
      items: [],
    } as ReceiptBankingRow;
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    (row: Partial<ReceiptBankingRow>, meta?: PersistMeta) => {
      const payload = normalizePayload(row);

      if (!payload.receipt_date || !payload.bank_account_id) {
        SnackbarUtils.error("Ngày thu và Tài khoản ngân hàng là bắt buộc");
        meta?.onRevert?.();
        return;
      }

      if (!payload.items?.length || !payload.items[0].amount) {
        SnackbarUtils.error("Vui lòng nhập số tiền thu");
        meta?.onRevert?.();
        return;
      }

      const shouldCreate = meta?.pinned || !row.id;
      if (shouldCreate) {
        createReceiptBanking.mutate(payload, {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(withItemFields(res.data as ReceiptBankingRow));
            }
            resetInlineAddRow();
            if (meta?.refetch !== false) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Thêm phiếu thu không thành công");
            meta?.onRevert?.();
          },
        });
        return;
      }

      if (!row.id) {
        SnackbarUtils.error("Không xác định được phiếu thu cần cập nhật");
        meta?.onRevert?.();
        return;
      }

      updateReceiptBanking.mutate(
        { id: row.id, ...payload },
        {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(withItemFields(res.data as ReceiptBankingRow));
            } else {
              upsertRow(
                withItemFields({ ...(row as ReceiptBankingRow), ...payload }),
              );
            }
            if (meta?.refetch) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Cập nhật phiếu thu không thành công");
            meta?.onRevert?.();
          },
        },
      );
    },
    [
      createReceiptBanking,
      normalizePayload,
      refetch,
      resetInlineAddRow,
      updateReceiptBanking,
      upsertRow,
      withItemFields,
    ],
  );

  const handleSaveRow = useCallback(
    (row: ReceiptBankingRow, meta?: { pinned?: boolean }) => {
      persistRow(row, {
        pinned: meta?.pinned,
        refetch: meta?.pinned || !row.id,
        onSuccess: () => {
          setShowInlineAddRow("");
          setEditingId(null);
        },
      });
    },
    [persistRow, setEditingId],
  );

  const handleEditRow = useCallback(
    (params) => {
      setShowInlineAddRow("edit");
      setEditingId(params.data.id);
    },
    [setEditingId],
  );

  const handleDeleteRow = useCallback((row: ReceiptBankingRow) => {
    setConfirmRow(row);
    setShowInlineAddRow("");
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteReceiptBanking.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        if (editingId === confirmRow.id) {
          setEditingId(null);
        }
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa phiếu thu không thành công");
      },
    });
  }, [
    confirmRow,
    deleteReceiptBanking,
    editingId,
    removeRow,
    setEditingId,
  ]);

  const toggleInlineAddRow = useCallback(() => {
    setEditingId(null);
    gridApiRef.current?.stopEditing();
    setShowInlineAddRow((prev) => {
      const next = !prev ? "add" : "";
      if (next) {
        resetInlineAddRow();
      } else {
        addRowRef.current = null;
        setAddRowVersion((v) => v + 1);
      }
      return next;
    });
  }, [resetInlineAddRow, setEditingId]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      editable: (params) =>
        params.data?.id === editingId || params.node.rowPinned === "top",
      flex: 1,
      minWidth: 140,
    }),
    [editingId],
  );

  const inlineAddRowData = useMemo(
    () => (showInlineAddRow ? (addRowRef.current ?? undefined) : undefined),
    [showInlineAddRow, addRowVersion],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">
          [Accounting] Receipt Banking
        </h1>
        <p className="text-sm text-gray-600">
          Quản lý phiếu thu ngân hàng: tạo mới, chỉnh sửa nhanh trên bảng và xoá khi cần.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách phiếu thu ngân hàng
            </h2>
            <p className="text-xs text-gray-500">
              Sử dụng grid để lọc, sắp xếp và chỉnh sửa trực tiếp.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleInlineAddRow}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4 text-green-700" />
              {showInlineAddRow ? "Ẩn dòng thêm mới" : "Thêm phiếu thu"}
            </button>
          </div>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          totalPages={receiptBankingData?.totalPage}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onSave={handleSaveRow}
          onAfterActions={() => {
            setShowInlineAddRow("");
            setEditingId(null);
          }}
          getRowId={(params) => String(params.data.id)}
          onQueryChange={handleQueryChange}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách phiếu thu ngân hàng, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa phiếu thu?"
        description={`Bạn chắc chắn muốn xóa phiếu thu "${confirmRow?.receipt_code ?? ""
          }"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteReceiptBanking.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default ReceiptBankings;
